import type { Coordinates3D, IsoDate } from './common';
import type { AllocationId, OrderId } from './ids';
import type { MarketplaceChannel } from './marketplace';
import type { PickingRouteTask, PickProductInfo, RoutingAlgorithm } from './picking';
import type { WarehouseLayout } from './warehouse';

/** Anything with a bin address that can be put on a route. */
export interface Routable {
  readonly code: string;
  readonly aisle: string;
  readonly rack: number;
  readonly level: number;
  readonly bin: number;
  readonly coordinates: Coordinates3D | null;
  readonly pickSequence: number | null;
}

/** A place on the floor: in an aisle, or outside the racks (depot, packing table: aisle null). */
export interface FloorPoint {
  readonly aisle: string | null;
  readonly at: Coordinates3D;
}
type Point = FloorPoint;

function axes(layout: WarehouseLayout) {
  return layout.aisleAxis === 'x'
    ? { along: (p: Coordinates3D) => p.x, across: (p: Coordinates3D) => p.y }
    : { along: (p: Coordinates3D) => p.y, across: (p: Coordinates3D) => p.x };
}

/**
 * Two rack points reached from the same walkway: the same row, or rows facing each other
 * across it (their pickers stand on the same line).
 */
function sameWalkway(a: Point, b: Point, across: (p: Coordinates3D) => number): boolean {
  return a.aisle !== null && b.aisle !== null && (a.aisle === b.aisle || Math.abs(across(a.at) - across(b.at)) < 0.05);
}

/**
 * Walking distance on a parallel-aisle floor plan. Within one walkway you walk along it;
 * to another one (or from the depot) you leave through whichever cross-aisle is shorter.
 */
export function walkingDistance(a: Point, b: Point, layout: WarehouseLayout, crossAisles: readonly number[]): number {
  const { along, across } = axes(layout);
  const sideways = Math.abs(across(a.at) - across(b.at));
  if (sameWalkway(a, b, across) || crossAisles.length === 0) {
    return Math.abs(along(a.at) - along(b.at)) + sideways;
  }
  return sideways + Math.min(...crossAisles.map((c) => Math.abs(along(a.at) - c) + Math.abs(along(b.at) - c)));
}

/**
 * Where the picker actually walks between two points, as floor-level waypoints: the same
 * rule as walkingDistance (along the aisle, or out through the shorter cross-aisle).
 */
export function walkingPath(a: FloorPoint, b: FloorPoint, layout: WarehouseLayout, crossAisles: readonly number[]): Coordinates3D[] {
  const { along, across } = axes(layout);
  const at = (alongValue: number, acrossValue: number): Coordinates3D =>
    layout.aisleAxis === 'x' ? { x: alongValue, y: acrossValue, z: 0 } : { x: acrossValue, y: alongValue, z: 0 };
  const from = at(along(a.at), across(a.at));
  const to = at(along(b.at), across(b.at));
  if (sameWalkway(a, b, across) || crossAisles.length === 0) {
    return across(a.at) === across(b.at) ? [from, to] : [from, at(along(b.at), across(a.at)), to];
  }
  const cost = (c: number) => Math.abs(along(a.at) - c) + Math.abs(along(b.at) - c);
  const cross = crossAisles.reduce((best, c) => (cost(c) < cost(best) ? c : best));
  return [from, at(cross, across(a.at)), at(cross, across(b.at)), to];
}

/** Cross-aisles from the layout, or the front and back of the racks the stops are in. */
export function crossAislesFor(layout: WarehouseLayout, stops: readonly Pick<Routable, 'coordinates'>[]): number[] {
  if (layout.crossAisles && layout.crossAisles.length > 0) return [...layout.crossAisles];
  const { along } = axes(layout);
  const positions = stops.flatMap((s) => (s.coordinates ? [along(s.coordinates)] : []));
  return positions.length === 0 ? [] : [Math.min(...positions) - 1, Math.max(...positions) + 1];
}

export interface RouteOptions {
  readonly layout: WarehouseLayout;
  /** Depot / packing table the picker leaves from */
  readonly start: Coordinates3D | null;
  readonly algorithm?: RoutingAlgorithm;
  readonly returnToStart?: boolean;
}

export interface RouteResult<T> {
  readonly algorithm: RoutingAlgorithm;
  readonly stops: readonly T[];
  /** Distance from the previous stop (the start for the first one); null without coordinates */
  readonly legs: readonly (number | null)[];
  readonly totalDistanceM: number | null;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Orders the stops of a pick trip. Default: nearest neighbour improved by 2-opt when every
 * stop has floor coordinates, the configured pick sequence when every stop has one,
 * S-shape (serpentine through the aisles) otherwise, which needs nothing but the bin address.
 */
export function routeStops<T extends Routable>(stops: readonly T[], options: RouteOptions): RouteResult<T> {
  const located = stops.every((s) => s.coordinates !== null);
  const algorithm: RoutingAlgorithm =
    options.algorithm ??
    (located && options.start ? 'nearest_neighbor_2opt' : stops.every((s) => s.pickSequence !== null) ? 'sequence' : 's_shape');
  const cross = crossAislesFor(options.layout, stops);
  const point = (s: T): Point => ({ aisle: s.aisle, at: s.coordinates! });
  const start: Point | null = options.start ? { aisle: null, at: options.start } : null;
  const distance = (a: Point, b: Point) => walkingDistance(a, b, options.layout, cross);

  let ordered: T[];
  if (algorithm === 'nearest_neighbor_2opt') {
    if (!located || !start) throw new RangeError('nearest_neighbor_2opt needs floor coordinates for every stop and a start point');
    ordered = nearestNeighbor2opt(stops, start, point, distance, options.returnToStart ?? false);
  } else if (algorithm === 'sequence') {
    ordered = [...stops].sort((a, b) => (a.pickSequence ?? Infinity) - (b.pickSequence ?? Infinity) || a.code.localeCompare(b.code));
  } else {
    ordered = sShape(stops, options.layout);
  }

  if (!located || !start) return { algorithm, stops: ordered, legs: ordered.map(() => null), totalDistanceM: null };
  const legs = ordered.map((s, i) => round2(distance(i === 0 ? start : point(ordered[i - 1]!), point(s))));
  const back = options.returnToStart && ordered.length > 0 ? distance(point(ordered.at(-1)!), start) : 0;
  return { algorithm, stops: ordered, legs, totalDistanceM: round2(legs.reduce((sum, l) => sum + l, 0) + back) };
}

/** Serpentine: aisles in floor order, walking up one aisle and down the next. */
function sShape<T extends Routable>(stops: readonly T[], layout: WarehouseLayout): T[] {
  const { along, across } = axes(layout);
  const byAisle = new Map<string, T[]>();
  for (const s of stops) byAisle.set(s.aisle, [...(byAisle.get(s.aisle) ?? []), s]);
  const aisles = [...byAisle.entries()].sort(([a, sa], [b, sb]) => {
    const ca = sa[0]!.coordinates;
    const cb = sb[0]!.coordinates;
    return ca && cb ? across(ca) - across(cb) || a.localeCompare(b) : a.localeCompare(b);
  });
  const position = (s: T) => (s.coordinates ? along(s.coordinates) : s.rack);
  return aisles.flatMap(([, inAisle], i) => {
    const up = [...inAisle].sort((a, b) => position(a) - position(b) || a.level - b.level || a.bin - b.bin || a.code.localeCompare(b.code));
    return i % 2 === 0 ? up : up.reverse();
  });
}

function nearestNeighbor2opt<T extends Routable>(
  stops: readonly T[],
  start: Point,
  point: (s: T) => Point,
  distance: (a: Point, b: Point) => number,
  closed: boolean,
): T[] {
  const remaining = [...stops];
  const tour: T[] = [];
  let current = start;
  while (remaining.length > 0) {
    let best = 0;
    for (let i = 1; i < remaining.length; i++) {
      const d = distance(current, point(remaining[i]!)) - distance(current, point(remaining[best]!));
      if (d < -1e-9 || (Math.abs(d) <= 1e-9 && remaining[i]!.code < remaining[best]!.code)) best = i;
    }
    const [next] = remaining.splice(best, 1);
    tour.push(next!);
    current = point(next!);
  }

  // 2-opt: reverse a segment whenever that shortens the walk from the start (back to it when closed)
  const at = (i: number): Point | null => (i < 0 ? start : i >= tour.length ? (closed ? start : null) : point(tour[i]!));
  const d = (a: Point | null, b: Point | null) => (a === null || b === null ? 0 : distance(a, b));
  for (let pass = 0, improved = true; improved && pass < 100; pass++) {
    improved = false;
    for (let i = 0; i < tour.length - 1; i++) {
      for (let k = i + 1; k < tour.length; k++) {
        const delta = d(at(i - 1), at(k)) + d(at(i), at(k + 1)) - d(at(i - 1), at(i)) - d(at(k), at(k + 1));
        if (delta < -1e-9) {
          tour.splice(i, k - i + 1, ...tour.slice(i, k + 1).reverse());
          improved = true;
        }
      }
    }
  }
  return tour;
}

/** One allocation to pick: a reserved quantity in a bin for an order line. */
export interface RouteAllocation {
  readonly allocationId: AllocationId;
  readonly orderId: OrderId;
  readonly orderNumber: string;
  /** the marketplace the order came from */
  readonly channel: MarketplaceChannel;
  readonly toteSlot: number | null;
  readonly qty: number;
  readonly lotNo: string | null;
  readonly expiryDate: IsoDate | null;
  readonly product: PickProductInfo;
  readonly location: PickingRouteTask['location'] & { readonly pickSequence: number | null };
}

/**
 * Turns the allocations of one or more orders into pick tasks: one stop per bin, product
 * and lot (batch picking takes a product once and sorts it into the orders' tote slots),
 * in walking order.
 */
export function planPickTasks(
  allocations: readonly RouteAllocation[],
  options: RouteOptions,
): { algorithm: RoutingAlgorithm; totalDistanceM: number | null; tasks: PickingRouteTask[] } {
  const groups = new Map<string, RouteAllocation[]>();
  for (const a of allocations) {
    const key = [a.location.id, a.product.productId, a.lotNo ?? '', a.expiryDate ?? ''].join('|');
    groups.set(key, [...(groups.get(key) ?? []), a]);
  }
  const stops = [...groups.values()].map((group) => {
    const first = group[0]!;
    const { pickSequence, ...location } = first.location;
    return {
      code: location.code,
      aisle: location.aisle,
      rack: location.rack,
      level: location.level,
      bin: location.bin,
      coordinates: location.coordinates,
      pickSequence,
      location,
      group,
    };
  });
  const route = routeStops(stops, options);
  const tasks = route.stops.map(
    (stop, i): PickingRouteTask => ({
      sequence: i + 1,
      location: stop.location,
      product: stop.group[0]!.product,
      lotNo: stop.group[0]!.lotNo,
      expiryDate: stop.group[0]!.expiryDate,
      quantity: stop.group.reduce((sum, a) => sum + a.qty, 0),
      distribution: [...stop.group]
        .sort((a, b) => (a.toteSlot ?? 0) - (b.toteSlot ?? 0))
        .map((a) => ({ orderId: a.orderId, orderNumber: a.orderNumber, channel: a.channel, allocationId: a.allocationId, toteSlot: a.toteSlot, quantity: a.qty })),
      legDistanceM: route.legs[i] ?? null,
      status: 'pending',
    }),
  );
  return { algorithm: route.algorithm, totalDistanceM: route.totalDistanceM, tasks };
}
